import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Button,
    useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Axios from "axios";
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../../theme";
import { getAuthHeader } from "../../../utils/authToken";
import dayjs from "dayjs";
import Loading from "../../../components/Loading";
import getCookies from "../../../utils/getCookies";
import { useSnackbar } from "notistack";
import ManageCustomExamsModal from "./ManageCustomExamsModal";

const TEST_TYPE_LABELS = {
    0: "SAT",
    1: "ACT",
    2: "AP",
    3: "PSAT",
    10: "Weekly SAT",
    11: "Weekly ACT",
    12: "Weekly AP",
    20: "Half SAT",
    21: "Half ACT",
    22: "Half AP",
    23: "Half PSAT",
};

const ClassroomETTReports = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const signOut = useSignOut();
    const navigate = useNavigate();

    const { enqueueSnackbar } = useSnackbar();
    const currentUser = getCookies("_auth_state");
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const courseId = window.location.pathname.split('/')[2];

    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [exams, setExams] = useState([]);
    const [ettUserId, setEttUserId] = useState(null);
    const [examsLoading, setExamsLoading] = useState(false);
    const [customExamsModalOpen, setCustomExamsModalOpen] = useState(false);

    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    };

    useEffect(() => {
        // Students can't access this page
        if (userRole === "student") {
            window.location.href = `/classrooms/${courseId}/home`;
            return;
        }

        localStorage.setItem('selectedMenu', 'ETT Reports');

        async function fetchStudents() {
            try {
                const response = await Axios.get(
                    `${process.env.REACT_APP_URL}/api/v1/ett-reports/course/${courseId}/students`,
                    { headers: { Authorization: getAuthHeader() } }
                );
                setStudents(response.data);
            } catch (error) {
                if (error.response?.status === 401) {
                    handleLogout();
                } else {
                    console.error("Fetch students error:", error);
                    setStudents([]);
                }
            } finally {
                setLoading(false);
            }
        }

        fetchStudents();
    }, []);

    const handleSelectStudent = async (student) => {
        setSelectedStudent(student);
        setExamsLoading(true);
        setExams([]);
        setEttUserId(null);

        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/ett-reports/course/${courseId}/student/${student.id}/exams`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setEttUserId(response.data.ettUserId);
            setExams(response.data.exams);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error("Fetch exams error:", error);
                setExams([]);
            }
        } finally {
            setExamsLoading(false);
        }
    };

    const handleBack = () => {
        setSelectedStudent(null);
        setExams([]);
        setEttUserId(null);
    };

    const handleViewReport = (exam) => {
        navigate(`/classrooms/${courseId}/ett-reports/${ettUserId}/${exam.test_id}?studentId=${selectedStudent.id}`);
    };

    const handleToggleHidden = async (exam) => {
        try {
            if (exam.hidden) {
                await Axios.delete(
                    `${process.env.REACT_APP_URL}/api/v1/ett-reports/course/${courseId}/student/${selectedStudent.id}/hidden-reports/${exam.test_id}`,
                    { headers: { Authorization: getAuthHeader() } }
                );
            } else {
                await Axios.post(
                    `${process.env.REACT_APP_URL}/api/v1/ett-reports/course/${courseId}/student/${selectedStudent.id}/hidden-reports`,
                    { ett_exam_id: exam.test_id },
                    { headers: { Authorization: getAuthHeader() } }
                );
            }
            setExams(prev => prev.map(e =>
                e.test_id === exam.test_id ? { ...e, hidden: !e.hidden } : e
            ));
            enqueueSnackbar(
                exam.hidden ? "Report visible to teachers" : "Report hidden from teachers",
                { variant: "success" }
            );
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error("Toggle hidden error:", error);
                enqueueSnackbar("Failed to update report visibility", { variant: "error" });
            }
        }
    };

    const getScoreDisplay = (session) => {
        const type = session.test_type;
        if (type === 1) {
            const scores = [session.score_1, session.score_2, session.score_3, session.score_4].filter(s => s > 0);
            const composite = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
            return `${composite} (E:${session.score_1} M:${session.score_2} R:${session.score_3} S:${session.score_4})`;
        }
        const total = (session.score_1 || 0) + (session.score_2 || 0);
        return `${total} (RW:${session.score_1} M:${session.score_2})`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    if (loading) return <Loading />;

    return (
        <Box m="20px">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
                <Box display="flex" alignItems="center" gap="10px">
                    {selectedStudent && (
                        <IconButton onClick={handleBack}>
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <Typography variant="h4" fontWeight="bold">
                        ETT Reports
                    </Typography>
                </Box>
                {!selectedStudent && userRole === 'admin' && (
                    <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={() => setCustomExamsModalOpen(true)}
                    >
                        Manage Custom Exams
                    </Button>
                )}
            </Box>

            {!selectedStudent ? (
                // Student list view
                <Box>
                    {students.length === 0 ? (
                        <Typography color={colors.grey[300]}>No students enrolled.</Typography>
                    ) : (
                        <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600] }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Korean Name</TableCell>
                                        <TableCell>ETT Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {students.map((student) => {
                                        const hasEtt = !!student.ett_email;
                                        const isDropped = student.end_date && dayjs(student.end_date).isBefore(dayjs());
                                        return (
                                            <TableRow
                                                key={student.id}
                                                hover={hasEtt}
                                                sx={{
                                                    cursor: hasEtt ? "pointer" : "default",
                                                    opacity: hasEtt ? 1 : 0.5,
                                                    textDecoration: isDropped ? "line-through" : "none",
                                                }}
                                                onClick={() => hasEtt && handleSelectStudent(student)}
                                            >
                                                <TableCell>
                                                    {student.first_name} {student.last_name}
                                                </TableCell>
                                                <TableCell>
                                                    {student.korean_name || "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {hasEtt ? (
                                                        <Chip
                                                            label={student.ett_email}
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label="No ETT account linked"
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            ) : (
                // Selected student - exam list view
                <Box>
                    <Box display="flex" alignItems="center" gap="15px" mb="20px">
                        <Box>
                            <Typography variant="h5" fontWeight="bold">
                                {selectedStudent.first_name} {selectedStudent.last_name}
                            </Typography>
                            <Typography variant="body2" color={colors.grey[300]}>
                                {selectedStudent.ett_email}
                            </Typography>
                        </Box>
                    </Box>

                    {examsLoading ? (
                        <Loading />
                    ) : exams.length === 0 ? (
                        <Typography color={colors.grey[300]}>No matching exams found.</Typography>
                    ) : (
                        <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600] }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Test Name</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Score</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {exams.map((exam) => (
                                        <TableRow
                                            key={exam.id}
                                            hover
                                            sx={{
                                                opacity: exam.hidden ? 0.4 : 1,
                                            }}
                                        >
                                            <TableCell>{exam.test_title}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={TEST_TYPE_LABELS[exam.test_type] || `Type ${exam.test_type}`}
                                                    size="small"
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>{getScoreDisplay(exam)}</TableCell>
                                            <TableCell>{formatDate(exam.finished_at)}</TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap="5px">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleViewReport(exam)}
                                                    >
                                                        View Report
                                                    </Button>
                                                    {userRole === 'admin' && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleToggleHidden(exam)}
                                                            title={exam.hidden ? "Unhide from teachers" : "Hide from teachers"}
                                                        >
                                                            {exam.hidden ? (
                                                                <VisibilityOffIcon fontSize="small" color="warning" />
                                                            ) : (
                                                                <VisibilityIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}

            {userRole === 'admin' && (
                <ManageCustomExamsModal
                    open={customExamsModalOpen}
                    onClose={() => setCustomExamsModalOpen(false)}
                    courseId={courseId}
                />
            )}
        </Box>
    );
};

export default ClassroomETTReports;
